import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
const _excluded = ["title", "className"],
  _excluded2 = ["setting", "json", "refreshAfterSaving"];
import app from '../app';
import Page from '../../common/components/Page';
import Button from '../../common/components/Button';
import classList from '../../common/utils/classList';
import Stream from '../../common/utils/Stream';
import saveSettings from '../utils/saveSettings';
import AdminHeader from './AdminHeader';
import FormGroup from '../../common/components/FormGroup';
import extractText from '../../common/utils/extractText';
import LoadingModal from './LoadingModal';

/**
 * Valid attrs that can be returned by the `headerInfo` function
 */

export default class AdminPage extends Page {
  constructor() {
    super(...arguments);
    _defineProperty(this, "settings", {});
    _defineProperty(this, "settingLabels", {});
    _defineProperty(this, "refreshAfterSaving", []);
    _defineProperty(this, "loading", false);
  }
  view(vnode) {
    const className = classList('AdminPage', this.headerInfo().className);
    return m("div", {
      className: className
    }, this.header(vnode), m("div", {
      className: "container"
    }, this.content(vnode)));
  }

  /**
   * Returns the content of the AdminPage.
   */

  /**
   * Returns the submit button for this AdminPage.
   *
   * Calls `this.saveSettings` when the button is clicked.
   */
  submitButton() {
    return m(Button, {
      type: "submit",
      onclick: this.saveSettings.bind(this),
      className: "Button Button--primary",
      loading: this.loading,
      disabled: !this.isChanged()
    }, app.translator.trans('core.admin.settings.submit_button'));
  }

  /**
   * Returns a button that opens a confirmation modal to delete all settings
   * tracked by this page from the database, reverting them to their PHP-side defaults.
   *
   * Calls can pass an explicit list of setting keys to reset; otherwise all keys
   * currently tracked in `this.settings` are used.
   */
  resetButton(settings, title, extensionId) {
    if (settings === void 0) {
      settings = Object.keys(this.settings).map(key => ({
        key,
        label: this.settingLabels[key]
      }));
    }
    return m(Button, {
      className: "Button Button--danger",
      onclick: () => app.modal.show(() => import(/* webpackChunkName: 'admin/components/ResetExtensionSettingsModal', webpackMode: 'lazy-once' */ './ResetExtensionSettingsModal'), {
        settings,
        title,
        extensionId
      })
    }, app.translator.trans('core.admin.extension.reset_settings.button'));
  }

  /**
   * Returns the Header component for this AdminPage.
   */
  header(vnode) {
    const _this$headerInfo = this.headerInfo(),
      {
        title,
        className
      } = _this$headerInfo,
      headerAttrs = _objectWithoutProperties(_this$headerInfo, _excluded);
    return m(AdminHeader, Object.assign({
      className: className ? "".concat(className, "-header") : undefined
    }, headerAttrs), title);
  }

  /**
   * Returns the options passed to the AdminHeader component.
   */
  headerInfo() {
    return {
      className: '',
      icon: '',
      title: '',
      description: ''
    };
  }

  /**
   * `buildSettingComponent` takes a settings object and turns it into a component.
   * Depending on the type of input, you can set the type to 'bool', 'select', or
   * any standard <input> type. Any values inside the 'extra' object will be added
   * to the component as an attribute.
   *
   * Alternatively, you can pass a callback that will be executed in ExtensionPage's
   * context to include custom JSX elements.
   *
   * @example
   *
   * {
   *    setting: 'acme.checkbox',
   *    label: app.translator.trans('acme.admin.setting_label'),
   *    type: 'bool',
   *    help: app.translator.trans('acme.admin.setting_help'),
   *    className: 'Setting-item'
   * }
   *
   * @example
   *
   * {
   *    setting: 'acme.select',
   *    label: app.translator.trans('acme.admin.setting_label'),
   *    type: 'select',
   *    options: {
   *      'option1': 'Option 1 label',
   *      'option2': 'Option 2 label',
   *    },
   *    default: 'option1',
   * }
   *
   * @example
   *
   * () => {
   *   return <p>My cool component</p>;
   * }
   */
  buildSettingComponent(entry) {
    var _default;
    if (typeof entry === 'function') {
      return entry.call(this);
    }
    const {
        setting,
        json,
        refreshAfterSaving
      } = entry,
      attrs = _objectWithoutProperties(entry, _excluded2);
    const originalBidi = this.setting(setting, (_default = entry.default) != null ? _default : '');
    let bidi;
    if (json) {
      bidi = function (value) {
        if (arguments.length) {
          originalBidi(JSON.stringify(value));
        }
        const v = originalBidi();
        if (v) {
          return JSON.parse(v);
        }
        return v;
      };
    } else {
      bidi = originalBidi;
    }
    if (refreshAfterSaving) {
      this.refreshAfterSaving.push(setting);
    }
    return m(FormGroup, Object.assign({
      stream: bidi,
      getSetting: this.setting.bind(this)
    }, attrs));
  }

  /**
   * Called when `saveSettings` completes successfully.
   */
  onsaved() {
    this.loading = false;
    app.alerts.show({
      type: 'success'
    }, app.translator.trans('core.admin.settings.saved_message'));
  }

  /**
   * Called when `saveSettings` fails to complete.
   */
  onsavefailed() {
    this.loading = false;
  }

  /**
   * Returns a function that fetches the setting from the `app` global.
   *
   * An optional `label` can be provided to associate a human-readable label
   * with the setting key, which is used by `resetButton()` in the reset modal.
   */
  setting(key, fallback, label) {
    if (fallback === void 0) {
      fallback = '';
    }
    this.settings[key] = this.settings[key] || Stream(app.data.settings[key] || fallback);
    if (label !== undefined) {
      this.settingLabels[key] = label;
    }
    return this.settings[key];
  }

  /**
   * Returns a map of settings keys to values which includes only those which have been modified but not yet saved.
   */
  dirty() {
    const dirty = {};
    Object.keys(this.settings).forEach(key => {
      const value = this.settings[key]();
      if (value !== app.data.settings[key]) {
        dirty[key] = value;
      }
    });
    return dirty;
  }

  /**
   * Returns the number of settings that have been modified.
   */
  isChanged() {
    return Object.keys(this.dirty()).length;
  }

  /**
   * Saves the modified settings to the database.
   */
  saveSettings(e) {
    e.preventDefault();
    app.alerts.clear();
    this.loading = true;
    const dirty = this.dirty();
    return saveSettings(dirty).then(this.onsaved.bind(this)).then(() => {
      if (this.refreshAfterSaving.length && Object.keys(dirty).some(setting => this.refreshAfterSaving.includes(setting))) {
        app.modal.show(LoadingModal);
        window.location.reload();
      }
    }).catch(this.onsavefailed.bind(this));
  }
  static modelLocale() {
    return {
      'Flarum\\Discussion\\Discussion': extractText(app.translator.trans('core.admin.models.discussions')),
      'Flarum\\User\\User': extractText(app.translator.trans('core.admin.models.users')),
      'Flarum\\Post\\Post': extractText(app.translator.trans('core.admin.models.posts'))
    };
  }
}
flarum.reg.add('core', 'admin/components/AdminPage', AdminPage);flarum.reg.addChunkModule('852', '6294', 'core', 'admin/components/ResetExtensionSettingsModal');